import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Clock, Users, Building2, Globe2,
  Compass, MessageSquareText, Mic2, Lightbulb,
  Handshake, HeartHandshake, Users2, Sparkles,
  Video, Building, Laptop,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Clock, value: "100,000+", label: "Learning Hours Delivered", desc: "Interactive leadership, communication, and professional development programs delivered across diverse industries." },
  { icon: Users, value: "500+", label: "Enterprise Batches Delivered", desc: "Instructor-led learning experiences designed for managers, business leaders, and cross-functional teams." },
  { icon: Building2, value: "50+", label: "Fortune & High-Growth Companies Served", desc: "Organizations trust Skillsgain for leadership development, employee engagement, and business excellence programs." },
  { icon: Globe2, value: "25+", label: "Global Technology Partners", desc: "Learning initiatives supported through globally recognized partners and industry-aligned best practices." }
];

const LEARNING_OUTCOMES = [
  { icon: Compass, title: "Leadership & Team Management", desc: "Develop effective Leadership Skills through coaching, delegation, decision-making, emotional intelligence, and performance management to inspire high-performing teams." },
  { icon: MessageSquareText, title: "Communication Skills", desc: "Strengthen verbal, written, and interpersonal communication with practical Communication Skills Training that enhances collaboration, stakeholder interactions, and workplace effectiveness." },
  { icon: Mic2, title: "Presentation & Public Speaking", desc: "Deliver impactful presentations with confidence by mastering storytelling, executive communication, audience engagement, and persuasive presentation techniques." },
  { icon: Lightbulb, title: "Problem Solving & Critical Thinking", desc: "Enhance Problem Solving Skills through analytical thinking, structured decision-making, creativity, root cause analysis, and practical business scenarios." },
  { icon: Handshake, title: "Client Relationship Management", desc: "Develop long-term customer relationships through effective communication, active listening, stakeholder management, negotiation, and service excellence." },
  { icon: HeartHandshake, title: "Employee Engagement & Collaboration", desc: "Promote stronger Employee Engagement through collaborative learning, team motivation, trust-building, workplace communication, and inclusive leadership practices." },
  { icon: Users2, title: "Team Training Activities", desc: "Improve collaboration with interactive Team Training Activities, role plays, business simulations, group discussions, case studies, and experiential learning exercises." },
  { icon: Sparkles, title: "Professional & Behavioural Excellence", desc: "Strengthen workplace professionalism through business etiquette, adaptability, conflict resolution, time management, accountability, and a growth mindset for long-term career success." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Workshops", desc: "Interactive facilitator-led sessions featuring role plays, group discussions, breakout activities, and live coaching to strengthen workplace communication and leadership capabilities." },
  { icon: Building, title: "Classroom Immersion", desc: "Face-to-face workshops focused on experiential learning through business simulations, case studies, team challenges, and collaborative exercises." },
  { icon: Laptop, title: "Self-Directed Learning", desc: "On-demand access to bite-sized learning modules, reflection exercises, practical assignments, and skill assessments that encourage continuous professional development." },
  { icon: Users2, title: "Coaching & Blended Learning", desc: "A structured learning journey combining instructor-led workshops, one-on-one coaching, peer learning, workplace practice, and continuous feedback to reinforce behavioral skills." }
];

const BY_THE_NUMBERS = [
  { value: "250+", label: "Enterprise Clients" },
  { value: "500+", label: "Instructor-Led Workshops" },
  { value: "25+", label: "Global Technology Partners" },
  { value: "50+", label: "Fortune & High-Growth Companies Served" }
];

const SoftSkillsPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE SOFT SKILLS & LEADERSHIP LEARNING"
        title="Soft Skills & Leadership Training for High-Performing Teams"
        subtitle="Strengthen professional capabilities with Skillsgain's Soft Skill Development Courses designed for modern enterprises. Cover Leadership Skills Training, Communication Skills Training, Presentation Skills, Problem Solving Skills, Client Relationship Management, Employee Engagement, and collaborative Team Training Activities through instructor-led learning and practical workplace scenarios."
      />

      {/* Enterprise Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">ENTERPRISE LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Developing confident leaders and high-performing teams</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Professional Development Expertise */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">PROFESSIONAL DEVELOPMENT EXPERTISE</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Practical skills for leadership, collaboration, and business success</h2>
            <p className="mt-3 text-gray-600">Interactive workshops, role-based activities, business simulations, and real workplace scenarios help professionals improve communication, leadership, collaboration, and customer engagement.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Learning Experiences */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">LEARNING EXPERIENCES</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Learning formats designed for enterprise professionals</h2>
              <p className="mt-3 text-gray-600">Choose instructor-led virtual sessions, classroom workshops, self-paced learning, or blended programs that encourage collaboration, participation, and practical application.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + Customer Success Story */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#4F9622] mb-4"/>
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">CUSTOMER SUCCESS STORY</div>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain delivered a customized leadership and communication learning program that strengthened presentation skills, collaboration, employee engagement, and client interactions across our business teams. The practical workshops received outstanding feedback from participants and managers alike.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR HR & BUSINESS LEADERS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Professional Development for Every Stage of Leadership</h2>
                <p className="mt-3 text-gray-300">Help professionals strengthen communication, leadership, collaboration, customer engagement, and workplace effectiveness through enterprise Soft Skills Training programs.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default SoftSkillsPage;
